/** Shared CubicleChat — 3-room chat panel for every cubicle.
 *
 *  Three tabs, three audiences:
 *    • Team       — cubicle team only (dept crew + accepted sub-members).
 *    • Production — team + PH admins + Executive Master Keys
 *                   (producer, LP, AD, director) + custom invitees.
 *    • Private    — curated back-room, HOD / master / owner picks the
 *                   subset. Only visible if the caller is a member.
 *
 *  Uses the existing /messaging endpoints for send/list; real-time
 *  updates flow through the app-wide WebSocket hub.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, UserPlus, Users, Users2, Lock, Plus } from "lucide-react";

export default function CubicleChat({ projectId, cubicleKey, canInviteGroup }) {
  const [tab, setTab]           = useState("team");
  const [teamThread, setTeam]   = useState(null);
  const [groupThread, setGroup] = useState(null);
  const [privateThread, setPrivate] = useState(null);
  const [privateAvailable, setPrivateAvailable] = useState(false);
  const [canStartPrivate, setCanStartPrivate] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUid, setInviteUid]   = useState("");
  const [privateOpen, setPrivateOpen] = useState(false);
  const [privateInviteUid, setPrivateInviteUid] = useState("");

  const activeThread =
    tab === "team"       ? teamThread
    : tab === "group"    ? groupThread
    : /* private */        privateThread;

  useEffect(() => {
    api.post(`/projects/${projectId}/cubicles/${cubicleKey}/thread`)
       .then(({data}) => setTeam(data.thread))
       .catch(() => {});
    api.post(`/projects/${projectId}/cubicles/${cubicleKey}/group-thread`)
       .then(({data}) => setGroup(data.thread))
       .catch(() => {});
    // Iter-93c fix: probe existence with GET (read-only) so the
    // "Start Private" CTA is reachable for eligible managers on a
    // fresh cubicle. The POST create fires only when the manager
    // explicitly clicks Start.
    api.get(`/projects/${projectId}/cubicles/${cubicleKey}/private-thread`)
       .then(({data}) => { setPrivate(data.thread); setPrivateAvailable(true);
                            setCanStartPrivate(false); })
       .catch((e) => {
         if (e?.response?.status === 404) {
           // Not yet initialised — surface the Start CTA only if the
           // backend flagged the caller as an eligible manager.
           const detail = e.response?.data?.detail || {};
           setCanStartPrivate(!!detail.can_start);
           setPrivateAvailable(false);
         } else {
           // 403 → room exists but caller isn't a member → hide silently.
           setPrivateAvailable(false);
           setCanStartPrivate(false);
         }
       });
  }, [projectId, cubicleKey]);

  useEffect(() => {
    if (!activeThread) return;
    let alive = true;
    api.get(`/messaging/threads/${activeThread.id}/messages`)
       .then(({data}) => { if (alive) setMessages(data.items || []); })
       .catch(() => {});
    return () => { alive = false; };
  }, [activeThread?.id]);

  const send = async () => {
    if (!draft.trim() || !activeThread) return;
    setBusy(true);
    try {
      await api.post(`/messaging/threads/${activeThread.id}/messages`,
                     { body: draft.trim() });
      setDraft("");
      const { data } = await api.get(
        `/messaging/threads/${activeThread.id}/messages`);
      setMessages(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const inviteToGroup = async () => {
    if (!inviteUid.trim()) return;
    try {
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/group-thread/invite`,
        { user_id: inviteUid.trim() });
      toast.success("Added to group thread");
      setInviteUid(""); setInviteOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const startPrivateRoom = async () => {
    try {
      const { data } = await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/private-thread`);
      setPrivate(data.thread);
      setPrivateAvailable(true);
      setTab("private");
      toast.success("Private room opened — add teammates via the + button");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail)
        || "Only HOD / master keys can start the private room");
    }
  };

  const addToPrivate = async () => {
    if (!privateInviteUid.trim()) return;
    try {
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/private-thread/members`,
        { user_id: privateInviteUid.trim() });
      toast.success("Added to private room");
      setPrivateInviteUid(""); setPrivateOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="cs-card rounded-2xl p-5 mt-6"
         data-testid={`cubicle-chat-${cubicleKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setTab("team")}
                  className={"h-9 px-4 rounded-full text-[11px] tracking-[0.22em] uppercase inline-flex items-center gap-2 "
                           + (tab === "team"
                              ? "bg-white text-black"
                              : "border border-white/15 text-white/70 hover:text-white")}
                  data-testid={`chat-tab-team-${cubicleKey}`}>
            <Users size={12}/> Team
          </button>
          <button onClick={() => setTab("group")}
                  className={"h-9 px-4 rounded-full text-[11px] tracking-[0.22em] uppercase inline-flex items-center gap-2 "
                           + (tab === "group"
                              ? "bg-white text-black"
                              : "border border-white/15 text-white/70 hover:text-white")}
                  data-testid={`chat-tab-group-${cubicleKey}`}>
            <Users2 size={12}/> Production
          </button>
          {privateAvailable ? (
            <button onClick={() => setTab("private")}
                    className={"h-9 px-4 rounded-full text-[11px] tracking-[0.22em] uppercase inline-flex items-center gap-2 "
                             + (tab === "private"
                                ? "bg-white text-black"
                                : "border border-white/15 text-white/70 hover:text-white")}
                    data-testid={`chat-tab-private-${cubicleKey}`}>
              <Lock size={12}/> Private
            </button>
          ) : canStartPrivate && (
            <button onClick={startPrivateRoom}
                    className="h-9 px-4 rounded-full text-[11px] tracking-[0.22em] uppercase inline-flex items-center gap-2 border border-white/15 text-white/60 hover:text-white"
                    data-testid={`chat-start-private-${cubicleKey}`}>
              <Plus size={12}/> Start Private
            </button>
          )}
        </div>
        {tab === "group" && canInviteGroup && (
          <Button onClick={() => setInviteOpen(!inviteOpen)}
                  className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
                  data-testid={`chat-invite-toggle-${cubicleKey}`}>
            <UserPlus size={12}/> Invite
          </Button>
        )}
        {tab === "private" && canInviteGroup && (
          <Button onClick={() => setPrivateOpen(!privateOpen)}
                  className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
                  data-testid={`chat-private-invite-toggle-${cubicleKey}`}>
            <UserPlus size={12}/> Add
          </Button>
        )}
      </div>

      {inviteOpen && tab === "group" && (
        <div className="mt-3 flex gap-2" data-testid={`chat-invite-form-${cubicleKey}`}>
          <Input value={inviteUid}
                 onChange={(e) => setInviteUid(e.target.value)}
                 placeholder="User ID to add to production room"
                 className="bg-black/40 border-white/15 font-mono text-xs"
                 data-testid={`chat-invite-uid-${cubicleKey}`}/>
          <Button onClick={inviteToGroup}
                  className="btn-themed rounded-full px-4 uppercase tracking-[0.2em] text-[10px]"
                  data-testid={`chat-invite-submit-${cubicleKey}`}>
            Add
          </Button>
        </div>
      )}
      {privateOpen && tab === "private" && (
        <div className="mt-3 flex gap-2" data-testid={`chat-private-form-${cubicleKey}`}>
          <Input value={privateInviteUid}
                 onChange={(e) => setPrivateInviteUid(e.target.value)}
                 placeholder="Teammate's User ID (must be on this cubicle)"
                 className="bg-black/40 border-white/15 font-mono text-xs"
                 data-testid={`chat-private-uid-${cubicleKey}`}/>
          <Button onClick={addToPrivate}
                  className="btn-themed rounded-full px-4 uppercase tracking-[0.2em] text-[10px]"
                  data-testid={`chat-private-submit-${cubicleKey}`}>
            Add
          </Button>
        </div>
      )}

      <div className="mt-4 min-h-[180px] max-h-[280px] overflow-y-auto border border-white/8 rounded-lg p-3 bg-black/25 space-y-2"
           data-testid={`chat-thread-${cubicleKey}-${tab}`}>
        {messages.length === 0 && (
          <div className="text-xs text-white/40 text-center py-6">
            {tab === "team"       ? "No team messages yet."
             : tab === "group"    ? "No production-room messages yet."
             : /* private */        "No private messages yet — invite trusted teammates via the + button."}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className="flex flex-col"
               data-testid={`chat-message-${m.id}`}>
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">
              {m.sender_name || m.sender_id}
            </div>
            <div className="text-sm text-white/85">{m.body}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <Input value={draft}
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
               placeholder={`Message ${
                 tab === "team"       ? "the cubicle team"
                 : tab === "group"    ? "the production room"
                 : /* private */         "the private room"}…`}
               className="flex-1 bg-black/40 border-white/15"
               disabled={!activeThread || busy}
               data-testid={`chat-input-${cubicleKey}`}/>
        <Button onClick={send} disabled={!activeThread || busy || !draft.trim()}
                className="btn-themed rounded-full px-5 uppercase tracking-[0.22em] text-[11px]"
                data-testid={`chat-send-${cubicleKey}`}>
          <Send size={12}/>
        </Button>
      </div>
    </div>
  );
}
